import React, { useMemo } from 'react'
import { AppForms, variantProvider } from '@/app'
import { TypeGuards, useForm, useI18N, useModal, useState } from '@codeleap/common'
import { Button, Select, Text, TextInput, View, Calendar, dateformat, SliderOnboarding } from '@/components'
import { APIClient } from '@/services'
import { analytics } from '@/services/analytics/segment'
import { useAppSelector } from '@/redux'
import { useKeydown, objectPickBy } from '@/utils'
import { navigate } from 'gatsby'
import { format, subYears } from 'date-fns'
import { Organisation } from '@/features'
import { ERRORS } from '@/features/Auth/session/views'
import { aboutYouForm } from '@/features/Auth/forms/aboutYou'

type AboutYouFormProps = {
  isModal?: boolean
}

export const AboutYouForm = ({ isModal = true }: AboutYouFormProps) => {
  const { data: profile } = APIClient.Session.useProfile()
  const isFocused = useAppSelector(state => state?.AppStatus?.modals?.aboutYou)
  const reauthenticationModal = useModal()

  const { t } = useI18N()

  const { editProfile } = APIClient.Session.useEdit()
  const { data: options } = APIClient.Session.useProfileOptions()
  const onboarding = Organisation.Hooks.useOnboarding()

  const defaultDate = useMemo(() => {
    const date = new Date()

    return subYears(date, 30)
  }, [])

  const initialState = React.useMemo(
    () => ({
      ...profile,
      organization: !!profile?.organization
        ? {
            label: profile?.organization?.name,
            value: profile?.organization?.id,
            item: profile?.organization,
            ...profile?.organization
          }
        : null
    }),
    [profile]
  )

  const initialBirthday = !!initialState?.birthday ? new Date(initialState?.birthday) : null

  const [dateValue, _setDateValue] = useState(initialBirthday)

  const form = useForm(aboutYouForm, {
    // @ts-ignore
    initialState
  })

  const setDateValue = (date: Date) => {
    _setDateValue(date)
  }

  async function onSubmit() {
    const { organization, gender, country, satisfaction_at_work, ...rest } = form.values
    const data = {
      organization: TypeGuards.isNumber(organization) ? organization : organization?.id,
      gender,
      country,
      birthday: dateValue,
      satisfaction_at_work
    }

    analytics.track('about_you', {
      country: form.values.country,
      advertising_source: form.values.inbound_source,
      climate_priority: form.values.climate_priority,
      date_of_birth: dateValue,
      satisfaction_at_work: form.values.satisfaction_at_work
    })

    analytics.track('personal_details')

    try {
      const serializedData = objectPickBy(data, value => !TypeGuards.isNil(value))
      await editProfile(serializedData)
    } catch (e) {
      if (e === ERRORS.reautenticationRequired) {
        reauthenticationModal.toggle()
        return
      }
    }

    if (isModal) {
      onboarding.next()
    } else {
      navigate('/auth/aboutJob/')
    }
  }

  useKeydown(
    () => {
      const isInvalidSubmit = (isModal && !isFocused) || !form.isValid
      if (isInvalidSubmit) return
      onSubmit?.()
    },
    [isFocused, isModal, form?.values, dateValue],
    'enter'
  )

  const selectedInboundSource = options?.inbound_source.find(item => item.value === form.values.inbound_source)

  return (
    <View variants={['flex', 'column']}>
      <Text variants={['h1', 'extraBold', 'textCenter', 'marginBottom:4']} text={t('AboutYou.title')} />
      <Text variants={['p1', 'marginBottom:4', 'marginTop:-3', 'textCenter']} text={t('AboutYou.sub')} />

      <View variants={['column', 'gap:3', 'center']}>
        <View variants={['column', 'gap:1', 'fullWidth']}>
          <Calendar
            label={t('AboutYou.calendarLabel')}
            placeholder={format(defaultDate, dateformat)}
            value={dateValue}
            onValueChange={setDateValue}
            leftIcon={{ name: 'calendar' }}
            rightIcon={{ name: 'chevron-down' }}
            variants={['fullWidth']}
            style={styles.calendarWrapper}
            defaultValue={defaultDate}
          />
          <Select loadInitialValue {...form.register('gender')} options={options?.gender} variants={['fullWidth']} />
          <Select
            loadInitialValue
            {...form.register('country')}
            options={options?.country}
            variants={['fullWidth']}
            searchable
          />
          <Select {...form.register('inbound_source')} options={options?.inbound_source} variants={['fullWidth']} />

          {selectedInboundSource?.value === 'other' ? <TextInput {...form.register('inbound_source_details')} /> : null}

          <Select {...form.register('climate_priority')} options={options?.climate_priority} variants={['fullWidth']} />
          <SliderOnboarding {...form.register('satisfaction_at_work')} />
        </View>

        <View variants={['row', 'justifySpaceBetween']} style={styles.footer}>
          <Button
            text={t('AboutYou.continue')}
            disabled={!form.isValid || !form.values.inbound_source || !form.values.satisfaction_at_work}
            debugName={'Continue Button'}
            variants={['pill', 'large', 'neutral9', 'fullWidth', !form.isValid && 'outline']}
            onPress={onSubmit}
          />
        </View>
      </View>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    calendarWrapper: {
      zIndex: '1'
    },
    footer: {
      width: '50%',

      [theme.media.down('small')]: {
        ...theme.presets.fullWidth
      }
    }
  }),
  true
)
